import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Route,
  RouteComponentProps,
  Redirect,
  RouteProps,
  useLocation
} from 'react-router-dom';
import { setHeaderPanelExpand } from 'redux/app/actions';
import { setSysAdminMode } from 'redux/session/actions';
import {
  currentUser,
  selectIsAuthenticated,
  selectIsSysAdminMode
} from 'redux/session/selectors';
import { APP_URL, USERSROLE } from 'types';

const isValidUserRole = (
  userRoleGroup: string[],
  currentUserRole: string
): boolean => userRoleGroup.includes(currentUserRole) || false;

const AuthRoute: React.FC<
  RouteProps & { page: React.FC<RouteComponentProps>; requiredRole: string[] }
> = ({ page: Page, requiredRole, ...rest }: any) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector(currentUser);
  const isSysAdminMode = useSelector(selectIsSysAdminMode);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [localUserRole, setLocalUserRole] = useState<string | undefined>();
  const [forceRidrect, setForceRidrect] = useState<string | undefined>();

  useEffect(() => {
    dispatch(setHeaderPanelExpand({ isOpen: false }));
  }, [dispatch]);

  useEffect(() => {
    setLocalUserRole(user?.is_trial ? USERSROLE.TRIALUSER : user?.role_type);
    if (user?.is_trial) {
      if (user?.is_eula_accepted === false) {
        return setForceRidrect(APP_URL.EULA);
      }
      if (
        !user?.is_linked ||
        !user?.trial_organisation_name ||
        !user?.mobile_number
      ) {
        return setForceRidrect(APP_URL.ONBOARDING);
      }
    }
  }, [user]);

  useEffect(() => {
    if (
      user?.role_type === USERSROLE.SYSADMIN &&
      location.pathname.match(/^\/sysadmin/)
    ) {
      dispatch(setSysAdminMode(true));
    }
  }, [user, location, dispatch]);

  const renderSelectedRoute = () => (
    <Route
      {...rest}
      render={(props: RouteComponentProps) => <Page {...props} />}
    />
  );

  // DEFAULT URL ROUTE
  const renderDefaultRoute = () => <Redirect to={APP_URL.ROOT_URL} />;

  const renderSysAdminDefaultRoute = () => (
    <Redirect to={APP_URL.SYSADMIN_ORG} />
  );

  // UPGRADE URL ROUTE FOR TRIAL USERS
  const renderUpgradeRoute = (path: string) => (
    <Redirect to={`${APP_URL.UPGRADE}?preview=${path}`} />
  );

  const renderForceRedirectRoute = (path: string) => <Redirect to={path} />;

  const renderAuthenticatedRoute = () => {
    if (location.pathname.match(/^\/sysadmin/)) {
      if (localUserRole !== USERSROLE.SYSADMIN) {
        return renderDefaultRoute();
      }
    } else {
      if (isSysAdminMode) {
        return renderSysAdminDefaultRoute();
      }
    }

    if (localUserRole && isValidUserRole(requiredRole, localUserRole)) {
      return forceRidrect &&
        location.pathname !== forceRidrect &&
        location.pathname !== APP_URL.LOGOUT
        ? renderForceRedirectRoute(forceRidrect)
        : renderSelectedRoute();
    } else {
      const trialUserForbiddenURL = [
        'teams',
        'reporting',
        'users',
        'enquiries/new'
      ];
      const path = location.pathname.replace('/', '');
      if (
        localUserRole === USERSROLE.TRIALUSER &&
        trialUserForbiddenURL.includes(path)
      ) {
        return renderUpgradeRoute(path !== 'enquiries/new' ? path : 'create');
      }
      return renderDefaultRoute();
    }
  };

  const renderUnAuthenticatedRoute = () => (
    <Route
      {...rest}
      render={() => (
        <Redirect
          to={{
            pathname: APP_URL.AUTH,
            state: { from: `${location.pathname}${location.search}` }
          }}
        />
      )}
    />
  );

  return (
    <>
      {isAuthenticated
        ? localUserRole && renderAuthenticatedRoute()
        : renderUnAuthenticatedRoute()}
    </>
  );
};

AuthRoute.displayName = 'AuthRoute';
export default AuthRoute;
